import React, { useMemo } from "react";
import { Table, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { TableProps } from "antd";
import { useIsMobile, useTheme } from "../../Hook";

export interface TableV2Props<
  RecordType extends object = any,
> extends TableProps<RecordType> {
  mobileColumn?: string[];
}

const getNestedValue = (record: any, path: any): any => {
  if (!record || path === undefined || path === null) return undefined;
  
  if (Array.isArray(path)) {
    return path.reduce((acc, key) => acc?.[key], record);
  }
  
  const pathStr = String(path);
  if (!pathStr.includes(".")) {
    return record[pathStr];
  }
  
  return pathStr.split(".").reduce((acc, key) => acc?.[key], record);
};

const renderCellContent = (column: any, record: any, index: number) => {
  try {
    const dataIndex = column.dataIndex;
    const value = dataIndex !== undefined ? getNestedValue(record, dataIndex) : undefined;
    
    if (typeof column.render === "function") {
      return column.render(value, record, index);
    }
    
    return value ?? "-";
  } catch (error) {
    console.error(
      `[TableV2] Error rendering cell content for column "${column.key || column.dataIndex || "unknown"}"`,
      error
    );
    return (
      <span className="text-red-500 text-xs font-semibold italic select-none" title="Render Error">
        Error
      </span>
    );
  }
};

export const TableV2 = <RecordType extends object = any>(
  props: TableV2Props<RecordType>,
) => {
  const { mobileColumn, columns, scroll, dataSource, ...rest } = props;
  const isMobile = useIsMobile();
  const { isDarkMode } = useTheme();
  const active = !!mobileColumn?.length && isMobile;

  const mobileColumns = useMemo(() => {
    if (!mobileColumn || mobileColumn.length === 0 || !columns) {
      return columns;
    }

    const columnMap = new Map<string, any>();
    columns.forEach((c: any) => {
      const key = c.key || c.dataIndex;
      if (key) {
        columnMap.set(String(key), c);
      }
    });

    const mobileColumnSet = new Set(mobileColumn.map(String));

    const ordered = mobileColumn
      .map((key) => columnMap.get(String(key)))
      .filter(Boolean);

    const hidden = columns.filter((c: any) => {
      const key = c.key || c.dataIndex;
      return !key || !mobileColumnSet.has(String(key));
    });

    if (hidden.length === 0) {
      return ordered;
    }

    return [
      ...ordered,
      {
        key: "__details__",
        title: "Details",
        align: "center",
        render: (_: any, record: any, index: number) => (
          <Tooltip
            placement={isMobile ? "right" : "left"}
            color={isDarkMode ? "#181d2a" : "white"}
            overlayStyle={{ pointerEvents: "none" }}
            overlayInnerStyle={{
              color: isDarkMode ? "#e9edf5" : "#1f2937",
              padding: "12px 16px",
              borderRadius: "10px",
              boxShadow: isDarkMode
                ? "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)"
                : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              border: `1px solid ${isDarkMode ? "#343b4d" : "#f3f4f6"}`,
              width: "320px",
              maxWidth: "90vw",
            }}
            title={
              <div className="pointer-events-none flex flex-col">
                {hidden.map((c: any) => {
                  const itemKey = c.key || c.dataIndex || String(c.title);
                  return (
                    <div
                      key={String(itemKey)}
                      className="grid grid-cols-[105px_1fr] gap-3 items-start py-2 border-b border-gray-100 dark:border-slate-800 last:border-0"
                    >
                      <span className="font-semibold text-gray-500 dark:text-slate-400 text-xs select-none">
                        {c.title}
                      </span>
                      <div className="text-gray-800 dark:text-slate-200 text-xs font-normal whitespace-normal break-words flex flex-col gap-0.5">
                        {renderCellContent(c, record, index)}
                      </div>
                    </div>
                  );
                })}
              </div>
            }
          >
            <InfoCircleOutlined className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-indigo-500 transition-colors" />
          </Tooltip>
        ),
      },
    ];
  }, [columns, mobileColumn, isMobile, isDarkMode]);

  const hasSumColumns = columns?.some((c: any) => c.sum);

  const columnSums = useMemo(() => {
    if (!hasSumColumns || !dataSource || dataSource.length === 0) {
      return {};
    }

    const sums: Record<string, number> = {};
    const activeColumns = active ? mobileColumns : columns;
    if (!activeColumns) return {};

    activeColumns.forEach((col: any, index: number) => {
      if (col.sum) {
        const key = col.key || col.dataIndex || String(index);
        let total = 0;
        if (typeof col.sum === "function") {
          try {
            total = col.sum(dataSource);
          } catch (err) {
            total = 0;
          }
        } else {
          const dataIndex = col.dataIndex;
          total = dataSource.reduce((sum: number, record: any) => {
            const val = dataIndex !== undefined ? getNestedValue(record, dataIndex) : 0;
            return sum + Number(val || 0);
          }, 0);
        }
        sums[String(key)] = total;
      }
    });
    return sums;
  }, [dataSource, columns, mobileColumns, active, hasSumColumns]);

  const autoSummary = useMemo(() => {
    if (props.summary || !hasSumColumns || !dataSource || dataSource.length === 0) {
      return props.summary;
    }

    return () => {
      const activeColumns = active ? mobileColumns : columns;
      if (!activeColumns) return null;

      let firstLabelIndex = activeColumns.findIndex((c: any) => !(c as any).sum);
      if (firstLabelIndex === -1) firstLabelIndex = 0;

      return (
        <Table.Summary fixed>
          <Table.Summary.Row className="bg-gray-50/50 dark:bg-slate-800/50 font-bold border-t border-gray-200 dark:border-slate-700">
            {activeColumns.map((col: any, index: number) => {
              const key = col.key || col.dataIndex || String(index);
              const isSum = !!col.sum;
              const align = col.align || "left";

              let content: React.ReactNode = "";
              if (index === firstLabelIndex) {
                content = <span className="font-bold text-xs text-gray-700 dark:text-slate-350">Total</span>;
              } else if (isSum) {
                const total = columnSums[String(key)] || 0;

                if (typeof col.render === "function") {
                  try {
                    content = col.render(total, { isSummary: true } as any, -1);
                  } catch (err) {
                    content = <span className="font-bold text-xs text-gray-800 dark:text-slate-200">{total}</span>;
                  }
                } else {
                  content = <span className="font-bold text-xs text-gray-800 dark:text-slate-200">{total}</span>;
                }
              }

              return (
                <Table.Summary.Cell 
                  key={String(key)} 
                  index={index} 
                  align={align}
                >
                  <div className="font-bold text-xs">
                    {content}
                  </div>
                </Table.Summary.Cell>
              );
            })}
          </Table.Summary.Row>
        </Table.Summary>
      );
    };
  }, [props.summary, hasSumColumns, columns, mobileColumns, active, dataSource, columnSums]);

  return (
    <Table
      {...rest}
      dataSource={dataSource}
      columns={active ? mobileColumns : columns}
      scroll={active ? { x: "max-content" } : scroll}
      summary={autoSummary}
    />
  );
};
